import {ONBOARDING_RESUME_STEP_MAP} from '@progyny/utils/ResumeMapping'
import {MENOPAUSE, PREGNANCY_AND_POSTPARTUM} from '@progyny/utils/constants'

import {UNSET} from '../const'

/** Minimal me.member shape for resume routing */
export type OnboardingResumeMember = {
  acceptedPrivacyStatement?: string | null
  acceptedTermsConditions?: string | null
  birthDate?: string | null
  city?: string | null
  activeMemberJourney?: string | null
  availableProductLines?: (string | null)[] | null
  birthAssignedSex?: string | null
  genderIdentity?: string | null
  relationshipDynamic?: string | null
  familyFormingJourney?: string | null
  menopauseJourney?: {
    menstrualPeriodStatus?: string | null
    lastMenstrualPeriod?: string | null
    menopauseSymptoms?: string | null | unknown
  } | null
  pregnancyPostpartumJourney?: {
    pregnancyPostpartumStatus?: string | null
    dueDate?: string | null
    deliveryDate?: string | null
  } | null
}

function isUnset(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true
  }
  if (Array.isArray(value) && value.length === 0) {
    return true
  }
  return value === UNSET || value === 'unset'
}

/**
 * Check at each route if data is present and stop to give a redirect when a gap in onboarding is found.
 * True when this resume-map step is still incomplete for member.
 */
function onboardingMissingStep(
  member: OnboardingResumeMember,
  step: (typeof ONBOARDING_RESUME_STEP_MAP)[number],
  productLine: string,
  isConservativeCountry: boolean
): boolean {
  const lines = member.availableProductLines ?? []
  const needsJourneySelection =
    lines.length > 1 && isUnset(member.activeMemberJourney)

  switch (step.parentRoute) {
    case '/create-account':
      switch (step.route) {
        case '/terms':
          return (
            isUnset(member.acceptedPrivacyStatement) ||
            isUnset(member.acceptedTermsConditions)
          )
        case '/birth-date':
          return isUnset(member.birthDate)
        case '/city':
          return isUnset(member.city)
        case '/complete':
          return needsJourneySelection
        default:
          return false
      }
    case '/select-journey':
      switch (step.route) {
        case '':
          return needsJourneySelection
        default:
          return false
      }
    case '/set-up-profile': {
      let next: string
      switch (productLine) {
        case MENOPAUSE: {
          const mj = member.menopauseJourney
          const menstrual = mj?.menstrualPeriodStatus
          const last = mj?.lastMenstrualPeriod
          const symptoms = mj?.menopauseSymptoms

          switch (true) {
            case isUnset(menstrual):
              next = '/menstrual-period'
              break
            case isUnset(last):
              next = '/last-menstrual'
              break
            case isUnset(symptoms):
              next = '/menstrual-symptoms'
              break
            default:
              next = '/complete'
          }
          break
        }
        case PREGNANCY_AND_POSTPARTUM: {
          const pj = member.pregnancyPostpartumJourney
          const status = pj?.pregnancyPostpartumStatus
          if (!pj || isUnset(status)) {
            next = '/pregnancy-info'
          } else {
            switch (status) {
              case 'pregnant': {
                const due = pj?.dueDate
                next = isUnset(due) ? '/pregnancy-date' : '/complete'
                break
              }
              case 'postpartum': {
                const delivery = pj?.deliveryDate
                next = isUnset(delivery) ? '/pregnancy-date' : '/complete'
                break
              }
              default:
                next = '/pregnancy-info'
            }
          }
          break
        }
        default: {
          if (isUnset(member.birthAssignedSex)) {
            next = '/birth-assigned-sex'
          } else if (isUnset(member.genderIdentity)) {
            next = isConservativeCountry
              ? '/relationship-dynamic'
              : '/gender-identity'
          } else if (isUnset(member.relationshipDynamic)) {
            next = '/relationship-dynamic'
          } else if (isUnset(member.familyFormingJourney)) {
            next = '/family-forming-journey'
          } else {
            next = '/complete'
          }
        }
      }

      const stepDone = next === '/complete' || next !== step.route
      return !stepDone
    }
    default:
      return false
  }
}

export type ResumeRouteResult = {
  /** Full paths (`${parentRoute}${route}`) of steps completed strictly before the first incomplete one. */
  completed: string[]
  /** First incomplete step's full path, or null if the whole onboarding is complete. */
  resumePath: string | null
}

/**
 * Walk {@link ONBOARDING_RESUME_STEP_MAP} (filtered by productLine) and return both
 * the list of already-completed step paths and the first incomplete step's path.
 */
export function getResumeRoute(
  member: OnboardingResumeMember | null | undefined,
  productLine: string,
  options: {isConservativeCountry: boolean}
): ResumeRouteResult {
  if (!member) {
    return {completed: [], resumePath: null}
  }
  const {isConservativeCountry} = options
  const steps = ONBOARDING_RESUME_STEP_MAP.filter(
    (s) => s.productLine === '*' || s.productLine === productLine
  )

  const completed: string[] = []
  let resumePath: string | null = null

  for (const step of steps) {
    const fullPath = `${step.parentRoute}${step.route}`
    if (
      onboardingMissingStep(member, step, productLine, isConservativeCountry)
    ) {
      resumePath = fullPath
      break
    }
    completed.push(fullPath)
  }

  return {completed, resumePath}
}
